import { useEffect, useMemo } from "react";
import { COMPANY, SITE_URL } from "@/config/company";

export interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  /** Geo meta etiketleri (local SEO) */
  geo?: boolean;
}

const DEFAULT_OG_IMAGE = COMPANY.showroomImage;

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  noindex = false,
  geo = true,
}: SEOProps) {
  const canonicalUrl = useMemo(
    () => canonical || SITE_URL + (typeof window !== "undefined" ? window.location.pathname : "/"),
    [canonical]
  );

  useEffect(() => {
    document.title = title;
    document.documentElement.lang = "tr";

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement("meta");
        const isProperty = selector.includes("[property=");
        if (isProperty) {
          el.setAttribute("property", selector.match(/"([^"]+)"/)?.[1] || "");
        } else {
          el.setAttribute("name", selector.match(/"([^"]+)"/)?.[1] || "");
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const ensureMeta = (name: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = content;
    };

    const ensureProperty = (property: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", property);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    ensureMeta("description", description);
    if (keywords) ensureMeta("keywords", keywords);
    ensureMeta("author", COMPANY.name);
    ensureMeta("robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

    if (geo) {
      ensureMeta("geo.region", COMPANY.geoRegion);
      ensureMeta("geo.placename", `${COMPANY.city}, ${COMPANY.district}`);
      ensureMeta("geo.position", `${COMPANY.lat};${COMPANY.lng}`);
      ensureMeta("ICBM", `${COMPANY.lat}, ${COMPANY.lng}`);
    }

    let canonicalEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.rel = "canonical";
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = canonicalUrl;

    ensureProperty("og:title", title);
    ensureProperty("og:description", description);
    ensureProperty("og:image", ogImage);
    ensureProperty("og:url", canonicalUrl);
    ensureProperty("og:type", ogType);
    ensureProperty("og:locale", "tr_TR");
    ensureProperty("og:site_name", COMPANY.name);

    ensureMeta("twitter:card", "summary_large_image");
    ensureMeta("twitter:title", title);
    ensureMeta("twitter:description", description);
    ensureMeta("twitter:image", ogImage);

    // AI / arama motorları için konum sinyali
    ensureMeta("application-name", COMPANY.name);

    void setMeta;
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, noindex, geo]);
}

/** Diyarbakır odaklı local anahtar kelime şablonu */
export function localKeywords(...terms: string[]): string {
  const base = [
    "diyarbakır",
    "kayapınar",
    "mayi mimarlık",
    "diyarbakır seramik",
    "diyarbakır vitrifiye",
    "diyarbakır yapı malzemeleri",
    "güneydoğu anadolu",
  ];
  return [...new Set([...terms, ...base])].join(", ");
}
